import { gradientStore, getGradientColors } from './gradient-store.js';

const HEX_HEIGHT_RATIO = 1.1547005;
const VISIBLE_HEX_ROWS = 2;
const BUFFER_HEX_ROWS = 1;
const DIAGONAL_HALF = Math.hypot(0.5, 0.5);
const PERM = [
  151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140, 36, 103, 30, 69, 142,
  8, 99, 37, 240, 21, 10, 23, 190, 6, 148, 247, 120, 234, 75, 0, 26, 197, 62, 94, 252, 219, 203,
  117, 35, 11, 32, 57, 177, 33, 88, 237, 149, 56, 87, 174, 20, 125, 136, 171, 168, 68, 175, 74, 165,
  71, 134, 139, 48, 27, 166, 77, 146, 158, 231, 83, 111, 229, 122, 60, 211, 133, 230, 220, 105, 92,
  41, 55, 46, 245, 40, 244, 102, 143, 54, 65, 25, 63, 161, 1, 216, 80, 73, 209, 76, 132, 187, 208,
  89, 18, 169, 200, 196, 135, 130, 116, 188, 159, 86, 164, 100, 109, 198, 173, 186, 3, 64, 52, 217,
  226, 250, 124, 123, 5, 202, 38, 147, 118, 126, 255, 82, 85, 212, 207, 206, 59, 227, 47, 16, 58,
  17, 182, 189, 28, 42, 223, 183, 170, 213, 119, 248, 152, 2, 44, 154, 163, 70, 221, 153, 101, 155,
  167, 43, 172, 9, 129, 22, 39, 253, 19, 98, 108, 110, 79, 113, 224, 232, 178, 185, 112, 104, 218,
  246, 97, 228, 251, 34, 242, 193, 238, 210, 144, 12, 191, 179, 162, 241, 81, 51, 145, 235, 249, 14,
  239, 107, 49, 192, 214, 31, 181, 199, 106, 157, 184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150,
  254, 138, 236, 205, 93, 222, 114, 67, 29, 24, 72, 243, 141, 128, 195, 78, 66, 215, 61, 156, 180,
];
const P = new Array(512).fill(0).map((_, i) => PERM[i & 255]);

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function hslToRgb({ h, s, l }) {
  const hue = ((h % 360) + 360) % 360;
  const sat = clamp(s, 0, 100) / 100;
  const light = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = light - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (hue < 60) {
    r = c;
    g = x;
  } else if (hue < 120) {
    r = x;
    g = c;
  } else if (hue < 180) {
    g = c;
    b = x;
  } else if (hue < 240) {
    g = x;
    b = c;
  } else if (hue < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

function mixRgb(start, end, t) {
  return {
    r: Math.round(start.r + (end.r - start.r) * t),
    g: Math.round(start.g + (end.g - start.g) * t),
    b: Math.round(start.b + (end.b - start.b) * t),
  };
}

function toRgbString({ r, g, b }) {
  return `rgb(${r}, ${g}, ${b})`;
}

function smoothstep01(value) {
  const t = clamp(value, 0, 1);

  return t * t * (3 - 2 * t);
}

function fade(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a, b, t) {
  return a + t * (b - a);
}

function grad(hash, x, y) {
  const h = hash & 7;
  const u = h < 4 ? x : y;
  const v = h < 4 ? y : x;

  return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
}

function perlin2(x, y) {
  const xi = Math.floor(x) & 255;
  const yi = Math.floor(y) & 255;
  const xf = x - Math.floor(x);
  const yf = y - Math.floor(y);

  const u = fade(xf);
  const v = fade(yf);

  const aa = P[P[xi] + yi];
  const ab = P[P[xi] + yi + 1];
  const ba = P[P[xi + 1] + yi];
  const bb = P[P[xi + 1] + yi + 1];

  const x1 = lerp(grad(aa, xf, yf), grad(ba, xf - 1, yf), u);
  const x2 = lerp(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u);

  return lerp(x1, x2, v);
}

export function initHeaderHexGrid() {
  const container = document.querySelector('.hex-hero');
  const grid = container ? container.querySelector('.hex-hero-grid') : null;

  if (!container || !grid) {
    return null;
  }

  let columns = 0;
  let rows = 0;
  let lastHexWidth = 0;
  let lastHexScale = 1;
  let hexes = [];
  let queued = false;

  function buildGrid() {
    const width = Math.max(1, container.clientWidth);
    const height = Math.max(1, container.clientHeight);
    const settings = gradientStore.getState().settings || {};
    const hexScale = clamp(Number(settings.hexScale ?? 1), 0.3, 3);
    const nextRows = VISIBLE_HEX_ROWS + BUFFER_HEX_ROWS * 2;
    const hexHeight = (height / (1 + 0.75 * (VISIBLE_HEX_ROWS - 1))) * hexScale;
    const hexWidth = hexHeight / HEX_HEIGHT_RATIO;
    const baseColumns = Math.max(1, Math.ceil(width / hexWidth));
    const nextColumns = baseColumns;
    const rowStep = hexHeight * 0.75;

    if (
      nextColumns === columns &&
      nextRows === rows &&
      Math.abs(lastHexWidth - hexWidth) < 0.5 &&
      Math.abs(lastHexScale - hexScale) < 0.001
    ) {
      return;
    }

    columns = nextColumns;
    rows = nextRows;
    lastHexWidth = hexWidth;
    lastHexScale = hexScale;
    grid.textContent = '';

    const fragment = document.createDocumentFragment();
    const nextHexes = [];

    for (let row = 0; row < rows; row += 1) {
      const globalRow = row - BUFFER_HEX_ROWS;
      const offset = globalRow % 2 === 0 ? 0 : -hexWidth / 2;
      const count = globalRow % 2 === 0 ? columns + 1 : columns + 2;
      const top = globalRow * rowStep;

      for (let col = 0; col < count; col += 1) {
        const hex = document.createElement('div');
        const left = offset + col * hexWidth;
        const centerX = left + hexWidth / 2;
        const centerY = top + hexHeight / 2;
        const x = clamp(centerX / width, 0, 1);
        const y = clamp(centerY / height, 0, 1);

        hex.className = 'hex-cell';
        hex.dataset.x = String(x);
        hex.dataset.y = String(y);
        hex.style.left = `${left}px`;
        hex.style.top = `${top}px`;
        hex.style.width = `${hexWidth}px`;
        hex.style.height = `${hexHeight}px`;
        fragment.appendChild(hex);
        nextHexes.push(hex);
      }
    }
    grid.appendChild(fragment);
    hexes = nextHexes;
  }

  function updateHexColors() {
    const state = gradientStore.getState();
    const { primaryColor, peekColor } = getGradientColors(state);
    const rgbStart = hslToRgb(primaryColor);
    const rgbEnd = hslToRgb(peekColor);
    const settings = state.settings || {};
    const time = Number(state.time || 0);
    const opacityMin = clamp(Number(settings.hexOpacityMin ?? 0.05), 0, 1);
    const opacityMax = clamp(Number(settings.hexOpacityMax ?? 1), 0, 1);
    const centerRadius = clamp(Number(settings.hexCenterRadius ?? 0.78), 0.05, 1.5);
    const centerEase = clamp(Number(settings.hexCenterEase ?? 1.8), 0.2, 6);
    const noiseAmp = clamp(Number(settings.hexNoiseAmp ?? 0.35), 0, 1);
    const noiseZoom = clamp(Number(settings.hexNoiseZoom ?? 3), 0.05, 20);
    const noiseEase = clamp(Number(settings.hexNoiseEase ?? 1.4), 0.2, 6);
    const noiseSpeed = clamp(Number(settings.hexNoiseSpeed ?? 0.25), 0, 10);
    const opacityLow = Math.min(opacityMin, opacityMax);
    const opacityHigh = Math.max(opacityMin, opacityMax);
    const drift = time * 0.01 * noiseSpeed;

    hexes.forEach(hex => {
      const x = clamp(Number(hex.dataset.x || '0'), 0, 1);
      const y = clamp(Number(hex.dataset.y || '0'), 0, 1);
      const t = x;
      const dx = x - 0.5;
      const dy = y - 0.5;
      const centerDistance = Math.hypot(dx, dy) / DIAGONAL_HALF;
      const radial = Math.pow(smoothstep01(centerDistance / centerRadius), centerEase);
      const noiseValue = perlin2(x * noiseZoom + drift, y * noiseZoom + drift * 0.61);
      const noise01 = smoothstep01((noiseValue + 1) / 2);
      const shapedNoise = Math.pow(noise01, noiseEase);
      const combined = clamp((1 - noiseAmp) * radial + noiseAmp * shapedNoise, 0, 1);
      const opacity = lerp(opacityLow, opacityHigh, combined);
      const sampled = mixRgb(rgbStart, rgbEnd, t);

      hex.style.opacity = String(opacity);
      hex.style.setProperty('--hex-fill', toRgbString(sampled));
    });
  }

  function queueUpdate() {
    if (queued) {
      return;
    }
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      buildGrid();
      updateHexColors();
    });
  }

  const resizeHandler = () => {
    buildGrid();
    updateHexColors();
  };
  const debouncedResize = (() => {
    let timeoutId;

    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(resizeHandler, 120);
    };
  })();
  const resizeObserver = window.ResizeObserver ? new ResizeObserver(debouncedResize) : null;
  const unsubscribe = gradientStore.subscribe(queueUpdate);

  buildGrid();
  updateHexColors();
  window.addEventListener('resize', debouncedResize);

  if (resizeObserver) {
    resizeObserver.observe(container);
  }

  return {
    destroy() {
      unsubscribe();
      window.removeEventListener('resize', debouncedResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    },
  };
}
